import { Document } from '@react-pdf/renderer'
import compact from 'lodash/compact'
import React, { useCallback, useMemo, useRef, useState } from 'react'

import { PDFDocumentProps } from './types/pdfTypes'

export const PDFDocument: React.FC<PDFDocumentProps> = ({
  report,
  onPageRender,
  initialSectionPages,
}) => {
  const [sectionPages, setSectionPages] = useState<Record<string, number>>(
    initialSectionPages || {}
  )

  const renderedPagesRef = useRef<Set<string>>(new Set())

  const handleRender = useCallback(
    ({ id, pageNumber }: { id: string; pageNumber: number }) => {
      // Only update if this page hasn't been rendered yet
      if (!renderedPagesRef.current.has(id)) {
        setSectionPages((prev) => ({ ...prev, [id]: pageNumber }))
        renderedPagesRef.current.add(id)

        // Call onPageRender only for new pages
        if (onPageRender) {
          onPageRender({ id, pageNumber })
        }
      }
    },
    [onPageRender]
  )

  const tocEntries = useMemo(() => {
    const flatSections = compact([
      report.coverSection,
      report.tocSection,
      ...report.sections,
      ...report.staticSections,
    ])

    return flatSections
      .filter((section) =>
        'showInTOC' in section ? section.showInTOC !== false : true
      )
      .map((section) => ({
        id: section.id,
        title: section.title,
      }))
  }, [
    report.coverSection,
    report.sections,
    report.staticSections,
    report.tocSection,
  ])

  return (
    <Document>
      {report.coverSection && (
        <report.coverSection.RenderPDF onRender={handleRender} />
      )}
      {
        <report.tocSection.RenderPDF
          entries={tocEntries}
          onRender={handleRender}
          sectionPages={sectionPages}
        />
      }
      {report.sections.map((section, index) => (
        <section.RenderPDF key={index} onRender={handleRender} />
      ))}
      {report.staticSections.map((section, index) => (
        <section.RenderPDF key={index} onRender={handleRender} />
      ))}
    </Document>
  )
}
